import React from 'react';
import { useTranslation } from 'react-i18next';

export type ConditionTone = 'good' | 'fair' | 'poor';

export type ConditionStat = {
  label: string;
  value: string;
  note?: string | null;
};

type ConditionSummaryStripProps = {
  eyebrow?: string;
  headline?: string;
  tone: ConditionTone;
  stats: ConditionStat[];
  actionLabel?: string;
  onAction?: () => void;
  compact?: boolean;
  verdictLabel?: string;
};

const toneClasses: Record<ConditionTone, string> = {
  good: 'border-[#90e0ef] bg-[#caf0f8] text-[#03045e]',
  fair: 'border-[#90e0ef] bg-white text-[#03045e]',
  poor: 'border-outline bg-surface-variant text-on-surface'
};

export const ConditionVerdictPill: React.FC<{ tone: ConditionTone; label?: string }> = ({ tone, label }) => {
  const { t } = useTranslation('common');

  return (
    <div className={`inline-flex rounded-full border px-3 py-1.5 text-sm font-semibold ${toneClasses[tone]}`}>
      {label || t(`conditionVerdicts.${tone}`)}
    </div>
  );
};

export const ConditionStatChip: React.FC<{ stat: ConditionStat; compact?: boolean }> = ({ stat, compact = false }) => (
  <div
    className={compact
      ? 'min-w-[82px] rounded-[15px] border border-outline/70 bg-surface px-3 py-2'
      : 'min-w-[108px] rounded-[18px] border border-outline/70 bg-surface px-3.5 py-2.5'}
  >
    <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-primary-500">{stat.label}</div>
    <div className="mt-1 text-sm font-semibold text-on-surface md:text-base">{stat.value}</div>
    {stat.note ? <div className="mt-0.5 text-xs text-on-surface-variant">{stat.note}</div> : null}
  </div>
);

const ConditionSummaryStrip: React.FC<ConditionSummaryStripProps> = ({
  eyebrow,
  headline,
  tone,
  stats,
  actionLabel,
  onAction,
  compact = false,
  verdictLabel
}) => {
  if (stats.length === 0 && !headline) return null;

  if (compact) {
    return (
      <div className="rounded-[18px] border border-outline/70 bg-surface px-3 py-3 shadow-sm md:rounded-[20px] md:px-4 md:py-3.5">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1.5">
            <div className="flex flex-wrap items-center gap-2">
              {eyebrow ? <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-primary-500">{eyebrow}</div> : null}
              <ConditionVerdictPill tone={tone} label={verdictLabel} />
            </div>
            {headline ? <div className="text-sm font-medium text-on-surface">{headline}</div> : null}
          </div>
          {actionLabel && onAction ? (
            <button
              type="button"
              onClick={onAction}
              className="shrink-0 pt-0.5 text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
            >
              {actionLabel}
            </button>
          ) : null}
        </div>
        {stats.length > 0 ? (
          <div className="mt-3 flex flex-wrap gap-2">
            {stats.map((stat) => (
              <ConditionStatChip key={`${stat.label}-${stat.value}`} stat={stat} compact />
            ))}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="rounded-[20px] border border-outline/70 bg-surface px-4 py-3 shadow-sm md:px-5 md:py-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            {eyebrow ? <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-primary-500">{eyebrow}</div> : null}
            <ConditionVerdictPill tone={tone} label={verdictLabel} />
          </div>
          {headline ? <div className="text-sm font-medium text-on-surface md:text-base">{headline}</div> : null}
        </div>

        <div className="flex flex-col gap-3 lg:items-end">
          {stats.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {stats.map((stat) => (
                <ConditionStatChip key={`${stat.label}-${stat.value}`} stat={stat} />
              ))}
            </div>
          ) : null}
          {actionLabel && onAction ? (
            <button
              type="button"
              onClick={onAction}
              className="text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
            >
              {actionLabel}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default ConditionSummaryStrip;
